import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import {
    CheckEmailStatusBindingModel, CheckEmailStatusViewModel, LogoutViewModel, UpdateDetailsBindingModel,
    UpdateDetailsViewModel, ChangePasswordBindingModel, SetPasswordViewModel, CustomerTransactionBindingModel,
    CustomerTransaction, UpdateCustomerBindingModel, UpdateCustomerViewModel, State, Country, Branch, ItemCode,
    BookTreatmentBindingModel, CancelAppointmentBindingModel, CancelAppointmentViewModel, ComputeOrderBindingModel,
    ComputeOrderViewModel, PlaceOrderBindingModel, PlaceOrderViewModel, ValidateGiftVoucherByNumberBindingModel,
    VerifyPendingTransactionBindingModel, Appointment, CancelPendingTransactionBindingModel, CommitPaymentBindingModel, CommitPaymentViewModel, Customer, IsValidEmailVerificationKeyBindingModel, IsValidEmailVerificationkeyViewModel, IsValidMigrationKeyBindingModel, IsValidMigrationKeyViewModel, IsValidPasswordResetKeyBindingModel, IsValidPasswordResetKeyViewModel, MigrateCustomerBindingModel, MigrateCustomerViewModel, RegisterCustomerBindingModel, ResetPasswordBindingModel, ResetPasswordViewModel, SendEmailVerificationBindingModel, SendEmailVerificationViewModel, SendMigrationLinkBindingModel, SendMigrationLinkViewModel, SendPasswordResetKeyLinkToEmailBindingModel, SendPasswordResetKeyLinkToEmailViewModel, SendTransactionToEmailBindingModel, SetTransactionAsPaidOnlineBindingModel, VerifyCustomerEmailBindingModel, VerifyCustomerEmailViewModel, AppointmentDate
} from '../models/web-booking.models';

import { BaseService, RequestType } from './base.service';

@Injectable()
export class WebBookingService extends BaseService {

    ValidateUserToken(): Observable<boolean> {
        return this.sendRequest('api/WebBooking/validate-user-token', null);
    }

    CheckEmailStatus(model: CheckEmailStatusBindingModel): Observable<CheckEmailStatusViewModel> {
        return this.sendRequest('api/WebBooking/check-email', model);
    }

    GetProfile(): Observable<Customer> {
        return this.sendRequest('api/WebBooking/get-profile', null, RequestType.Get);
    }

    Logout(): Observable<LogoutViewModel> {
        return this.sendRequest('api/WebBooking/logout', null);
    }

    SendMigrationLink(model: SendMigrationLinkBindingModel): Observable<SendMigrationLinkViewModel> {
        return this.sendRequest('api/WebBooking/send-migration-link', model);
    }

    SendEmailVerification(model: SendEmailVerificationBindingModel): Observable<SendEmailVerificationViewModel> {
        return this.sendRequest('api/WebBooking/send-email-verification', model);
    }

    SendPasswordResetKeyLinkToEmail(model: SendPasswordResetKeyLinkToEmailBindingModel):
        Observable<SendPasswordResetKeyLinkToEmailViewModel> {
        return this.sendRequest('api/WebBooking/send-password-reset-link-to-email', model);
    }

    GetHealthFunds(model: any): Observable<any[]> {
        return this.sendRequest('api/WebBooking/get-health-funds', model);
    }

    IsValidPasswordResetKey(model: IsValidPasswordResetKeyBindingModel): Observable<IsValidPasswordResetKeyViewModel> {
        return this.sendRequest('api/WebBooking/is-valid-password-reset-key', model);
    }

    ResetPassword(model: ResetPasswordBindingModel): Observable<ResetPasswordViewModel> {
        return this.sendRequest('api/WebBooking/reset-password', model);
    }

    RegisterCustomer(model: RegisterCustomerBindingModel): Observable<Customer> {
        return this.sendRequest('api/WebBooking/create-account', model);
    }

    UpdateDetails(model: UpdateDetailsBindingModel): Observable<UpdateDetailsViewModel> {
        return this.sendRequest('api/WebBooking/update-details', model);
    }

    SetPassword(model: ChangePasswordBindingModel): Observable<SetPasswordViewModel> {
        return this.sendRequest('api/WebBooking/set-password', model);
    }

    CustomerTransaction(model: CustomerTransactionBindingModel): Observable<CustomerTransaction> {
        return this.sendRequest('api/WebBooking/data', model);
    }

    IsValidMigrationKey(model: IsValidMigrationKeyBindingModel): Observable<IsValidMigrationKeyViewModel> {
        return this.sendRequest('api/WebBooking/is-valid-migrate-key', model);
    }

    MigrateCustomer(model: MigrateCustomerBindingModel): Observable<MigrateCustomerViewModel> {
        return this.sendRequest('api/WebBooking/migrate-customer', model);
    }

    UpdateCustomer(model: UpdateCustomerBindingModel): Observable<UpdateCustomerViewModel> {
        return this.sendRequest('api/WebBooking/update-customer', model);
    }

    ChangePassword(model: ChangePasswordBindingModel): Observable<boolean> {
        return this.sendRequest('api/WebBooking/change-password', model);
    }

    IsValidEmailVerificationkey(model: IsValidEmailVerificationKeyBindingModel): Observable<IsValidEmailVerificationkeyViewModel> {
        return this.sendRequest('api/WebBooking/is-valid-email-verification-key', model);
    }

    VerifyCustomerEmail(model: VerifyCustomerEmailBindingModel): Observable<VerifyCustomerEmailViewModel> {
        return this.sendRequest('api/WebBooking/verify-customer-email', model);
    }

    GetIncomingAppointments(): Observable<any[]> {
        return this.sendRequest('api/WebBooking/get-incoming-appointments', null);
    }

    GetTransactionHistory(): Observable<any[]> {
        return this.sendRequest('api/WebBooking/get-transaction-history', null);
    }

    GetStates(): Observable<State[]> {
        return this.sendRequest('api/webbooking/get-states', null, RequestType.Get);
    }

    GetCountries(): Observable<Country[]> {
        return this.sendRequest('api/webbooking/get-countries', null, RequestType.Get);
    }

    GetLocations(): Observable<Branch[]> {
        return this.sendRequest('api/webbooking/get-locations', null);
    }

    GetLocationById(model: any): Observable<Branch> {
        return this.sendRequest('api/webbooking/get-location-by-id', model);
    }

    GetTreatments(mode: any): Observable<ItemCode[]> {
        return this.sendRequest('api/webbooking/get-treatments', mode);
    }

    GetTreatmentByGroup(mode: any): Observable<any[]> {
        return this.sendRequest('api/webbooking/get-treatments-by-group', mode);
    }

    GetTherapists(mode: any): Observable<any[]> {
        return this.sendRequest('api/webbooking/get-therapists', mode);
    }

    GetAvailableSlots(model: any): Observable<AppointmentDate[]> {
        return this.sendRequest('api/webbooking/get-available-slots', model);
    }

    GetAvailableFromSpecificStaffTimes(model: any): Observable<AppointmentDate[]> {
        return this.sendRequest('api/webbooking/get-available-slots-from-specific-staff-time', model);
    }

    GetAvailableSlotPerDay(model: any): Observable<any[]> {
        return this.sendRequest('api/webbooking/get-available-slot-per-day', model);
    }

    GetTreatment(model: any): Observable<any> {
        return this.sendRequest('api/webbooking/get-treatment', model);
    }

    BookTreatments(model: any): Observable<any> {
        return this.sendRequest('api/webbooking/book-treatment', model);
    }

    BookCouple(model: any[]): Observable<any> {
        return this.sendRequest('api/webbooking/book', model);
    }

    ConfirmAppointment(model: any): Observable<any> {
        return this.sendRequest('api/webbooking/confirm-appointment', model);
    }

    Inquire(model: any): Observable<any> {
        return this.sendRequest('api/webbooking/inquire', model);
    }

    CancelAppointment(model: any, requestType?: RequestType): Observable<any> {
        return this.sendRequest('api/webbooking/cancel-appointment', model, requestType);
    }

    IsAppointmentExpired(model: any): Observable<boolean> {
        return this.sendRequest('api/WebBooking/is-appointment-expired', model);
    }

    GetGiftVoucherRemaingBalance(model: any): Observable<number> {
        return this.sendRequest('api/webbooking/get-gift-voucher-remaing-balance', model);
    }

    Book(model: BookTreatmentBindingModel): Observable<Appointment> {
        return this.sendRequest('api/webbooking/check-availability', model);
    }

    HoleGiftVoucher(model: any): Observable<number> {
        return this.sendRequest('api/webbooking/hold-digital-glue-gift-voucher', model);
    }

    SendPreArrivalCard(model: any): Observable<number> {
        return this.sendRequest('api/webbooking/send-pre-arrival-card', model);
    }

    Cancel(model: CancelAppointmentBindingModel): Observable<CancelAppointmentViewModel> {
        return this.sendRequest('api/webbooking/cancel', model);
    }


    CancelPendingTransaction(model: CancelPendingTransactionBindingModel, requestType?: RequestType): Observable<boolean> {
        return this.sendRequest('api/webbooking/cancel-order', model, requestType);
    }

    ComputeOrder(model: ComputeOrderBindingModel): Observable<any> {
        return this.sendRequest('api/webbooking/compute-order', model);
    }

    PlaceOrder(model: PlaceOrderBindingModel): Observable<PlaceOrderViewModel> {
        return this.sendRequest('api/webbooking/place-order', model);
    }

    ValidateGiftVoucherByNumber(model: ValidateGiftVoucherByNumberBindingModel[]): Observable<any[]> {
        return this.sendRequest('api/webbooking/validate-gift-voucher-number', model);
    }

    VerifyPendingTransaction(model: VerifyPendingTransactionBindingModel): Observable<boolean> {
        return this.sendRequest('api/webbooking/verify-pending-transaction', model);
    }

    CommitPayment(model: any): Observable<CommitPaymentViewModel> {
        return this.sendRequest('api/webbooking/commit-payment', model);
    }

    SetTransactionAsPaidOnline(model: SetTransactionAsPaidOnlineBindingModel): Observable<any> {
        return this.sendRequest('api/webbooking/set-transaction-as-paid-online', model);
    }

    SetTransactionAsPaidOnlineByGv(model: any): Observable<any> {
        return this.sendRequest('api/webbooking/set-transaction-as-paid-by-gift-voucher', model);
    }

    SendTransactionToEmail(model: any): Observable<boolean> {
        return this.sendRequest('api/webbooking/email-transaction-details', model);
    }

    GetAppointmentWebBookingProcessStatus(model: any, requestType?: RequestType): Observable<any> {
        return this.sendRequest('api/webbooking/get-appointmet-booking-process-status', model, requestType);
    }

    GetTransactionProcessStatus(model: any, requestType?: RequestType): Observable<any> {
        return this.sendRequest('api/webbooking/get-transaction-process-status', model, requestType);
    }

    RetrieveAppointmentByAppointmentId(model: any): Observable<any> {
        return this.sendRequest('api/webbooking/RetrieveAppointmentByAppointmentId', model);
    }

    Reschedule(model: any): Observable<boolean> {
        return this.sendRequest('api/webbooking/reschedule', model);
    }    

    GetAvailableSlotsByAppointmentId(model: any): Observable<AppointmentDate[]> {
        return this.sendRequest('api/webbooking/get-available-slots-by-appointment-id', model);
    }

    GetAvailableSlotPerDayByAppointmentId(model: any): Observable<any[]> {
        return this.sendRequest('api/webbooking/get-available-slot-per-day-by-appointment-id', model);
    }

    CancelAppointmentByAppoitnmentId(model: any, requestType?: RequestType): Observable<any> {
        return this.sendRequest('api/webbooking/cancel-appointment-by-appointment-id', model, requestType);
    }

    ConfirmAppointmentByAppoitnmentId(model: any, requestType?: RequestType): Observable<any> {
        return this.sendRequest('api/webbooking/confirm-appointment-by-appointment-id', model, requestType);
    }

    GetTreatmentsByAppointmentId(model: any, requestType?: RequestType): Observable<any[]> {
        return this.sendRequest('api/WebBooking/get-treatments-by-appointment-id', model, requestType);
    }

    SendUpdateEmailVerificationCodeToCurrentEmail(): Observable<boolean> {
        return this.sendRequest('api/WebBooking/send-update-email-verification-code-to-current-email', null);
    }

    SendUpdateEmailVerificationCodeToNewEmail(): Observable<boolean> {
        return this.sendRequest('api/WebBooking/send-update-email-verification-code-to-new-email', null);
    }

    SendUpdateEmailVerificationCodeToEmail(): Observable<boolean> {
        return this.sendRequest('api/WebBooking/send-update-email-verification-code-to-email', null);
    }

    UpdateCustomerEmailAddress(model: any): Observable<any> {
        return this.sendRequest('api/WebBooking/update-email-address', model);
    }

    CancelUpdateCustomerEmailAddress(): Observable<any> {
        return this.sendRequest('api/WebBooking/cancel-update-email-address', null);
    }

    SendUpdateEmailVerification(model: SendEmailVerificationBindingModel): Observable<SendEmailVerificationViewModel> {
        return this.sendRequest('api/WebBooking/send-update-email-verification', model);
    }
}
